import { useEffect, useRef, useState } from 'react';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
// Context
import { StreamProvider } from 'context/StreamContext';
// Hocks
import withApollo from 'hocs/withApollo';
// Types
import { StreamStatus, StreamType, UserRole } from 'api/graphql-global-types';
// Hooks
import { useGetCurrUser, useGetStreamBySlug, useHideHubSpotChat } from 'hooks';
// Helpers
import { getTokenFromCookies } from 'helpers/cookies';
import { getEnvLink, getStreamPath } from 'helpers/routes';
import { hasStreamRecordsV2, getStreamRecordsFromV2 } from 'helpers/streams';
import {
  cleanDescription,
  generateVideoObjectSchema,
  getRoundedVideoDurationSeconds,
  getVideoMimeType,
} from 'helpers/structured-data';
// Layouts
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import SsrVideoElement from 'components/WatchStream/SsrVideoElement/SsrVideoElement';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
import StripeElements from 'layouts/StripeElements/StripeElements';
// UI3
import PurchaseSuccessModal from 'components/PurchaseSuccessModal/PurchaseSuccessModal';
// Ui
import FourDotsLoader from 'ui/FourDotsLoader/FourDotsLoader';
// Components
import FinishedStream from 'components/WatchStream/FinishedStream/FinishedStream';

const UnfinishedStream = dynamic(
  import('components/WatchStream/UnfinishedStream/UnfinishedStream'),
  { ssr: false }
);

const WatchStreamContent = (): JSX.Element | null => {
  useHideHubSpotChat();

  // Show purchase success modal if flag set in localStorage
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const flag = localStorage.getItem('showPurchaseSuccess');
      if (flag === 'true') {
        setShowPurchaseSuccess(true);
        localStorage.removeItem('showPurchaseSuccess');
      }
    }
  }, []);

  const handleClosePurchaseModal = () => {
    setShowPurchaseSuccess(false);
  };

  const { push, query: routerQuery } = useRouter();
  const { data, refetch, updateQuery, loading, error } = useGetStreamBySlug({
    options: {
      // `getDataFromTree` populates the Apollo cache during SSR, so the first
      // client render paints instantly from cache while the network refresh
      // runs in the background (`isLoading` below only blocks when there is
      // no data at all). The refresh is mandatory: SSR queries run without
      // the auth cookie, so the hydrated cache always says `isPurchased:
      // false`, and a stale cache (SPA navigation, bfcache) can still say
      // `streamStatus: Active` after the stream ended — the status
      // subscription only fires on transitions, so it never corrects data
      // that was already stale on mount.
      fetchPolicy: 'cache-and-network',
      // Don't let the policy degrade to `cache-first` (the app-wide default
      // `nextFetchPolicy`): in Apollo 3.7 a re-render while the initial
      // request is still in flight (e.g. the Me query resolving first)
      // re-observes with the downgraded policy, flips `loading` to false off
      // the stale cache, and the redirect below fires before the fresh
      // response lands.
      nextFetchPolicy: 'cache-and-network',
      // Keep `loading` truthful during refetches (incl. logout's resetStore)
      // so the redirect logic below never acts on a half-cleared cache.
      notifyOnNetworkStatusChange: true,
      errorPolicy: 'all',
    },
  });
  const { data: userData, loading: userDataLoading } = useGetCurrUser();

  const stream = data?.stream;
  const token = getTokenFromCookies();
  const hasRedirected = useRef(false);

  const redirectPath = getStreamPath(stream, 'streams');
  // Only show the full-page loader while we truly have nothing to render.
  // With `cache-and-network` the SSR-cached stream paints instantly while
  // `loading` stays true for the background refresh — flashing the loader
  // over already-visible content was the old behaviour and pushed LCP.
  const isLoading = (!data && loading) || (!userData && userDataLoading);
  const isUserAStreamer = userData?.me?.id === stream?.store?.id;
  const isUserAModerator = userData?.me?.role === UserRole.Moderator;
  const isUserACustomer = userData && !isUserAStreamer && !isUserAModerator;

  // Build OG video metadata for ended streams with records
  const streamRecord =
    stream && hasStreamRecordsV2(stream)
      ? getStreamRecordsFromV2(stream, false)[0]
      : null;

  const videoMeta =
    stream?.streamStatus === StreamStatus.Ended && streamRecord?.videoURL
      ? {
          url: streamRecord.videoURL,
          type: getVideoMimeType(streamRecord.videoURL),
          duration: getRoundedVideoDurationSeconds(streamRecord.duration),
        }
      : undefined;

  const isStreamEnded = stream?.streamStatus === StreamStatus.Ended;
  const canonicalUrl =
    stream?.store?.slug && stream?.slug
      ? `${getEnvLink()}${
          isStreamEnded
            ? getStreamPath(stream, 'watch-stream/video')
            : getStreamPath(stream, 'streams')
        }`
      : null;
  const streamWithRecords =
    stream &&
    hasStreamRecordsV2(stream) &&
    stream?.streamStatus === StreamStatus.Ended;

  // Track if we were watching a live stream. Only trust statuses seen after
  // the query settled — a live status read from a stale cache while the
  // background refresh is still running (e.g. the owner re-opens the page
  // after ending the stream) is not "watching it live", and counting it
  // would bounce the viewer to the profile instead of showing the replay.
  const wasStreamLive = useRef(false);
  useEffect(() => {
    if (
      !loading &&
      (stream?.streamStatus === StreamStatus.Active ||
        stream?.streamStatus === StreamStatus.Paused ||
        stream?.streamStatus === StreamStatus.Interrupted)
    ) {
      wasStreamLive.current = true;
    }
  }, [stream?.streamStatus, loading]);

  const streamJustEnded = isStreamEnded && wasStreamLive.current;

  // What the currently available data says about access — may be based on
  // stale/anonymous cache while `loading` is true, so it gates the loader
  // but never a redirect.
  const isUnavailableFromData = token?.accessToken
    ? stream && isUserACustomer && !stream?.isPurchased && !stream?.isFree
    : stream &&
      (!stream?.isFree ||
        (stream?.isFree && stream?.streamStatus === StreamStatus.Scheduled));

  // Wait for GetStream response before determining if stream is available
  const isWatchStreamNotAvailable = loading
    ? false // Don't redirect while still loading
    : isUnavailableFromData;

  const isStreamCancelled = stream?.streamStatus === StreamStatus.Cancelled;
  const isStreamHidden = stream?.isHidden;

  useEffect(() => {
    // redirect to host's streams page if stream is canceled or hidden
    if ((isStreamCancelled || isStreamHidden) && stream?.store?.slug) {
      push(`/${stream.store.slug}/streams`);
    }
  }, [isStreamCancelled, isStreamHidden, stream?.store?.slug, push]);

  // Redirect to stream page if query errored (e.g. unauthenticated user), otherwise 404
  useEffect(() => {
    if (!loading && !stream && !hasRedirected.current) {
      hasRedirected.current = true;
      if (error) {
        const { athleteSlug, streamSlug } = routerQuery;
        push(`/${athleteSlug}/streams/${streamSlug}`);
      } else {
        push('/404');
      }
    }
  }, [loading, stream, error, push, routerQuery]);

  useEffect(() => {
    if (streamJustEnded && stream?.type === StreamType.Stream) {
      push(`/${stream?.store?.slug}`);
    }

    if (isWatchStreamNotAvailable && !userDataLoading) {
      hasRedirected.current = true;
      push(redirectPath);
      return;
    }

    return;
  }, [
    stream,
    push,
    isWatchStreamNotAvailable,
    redirectPath,
    streamJustEnded,
    userDataLoading,
  ]);

  // Don't render stream components if redirecting
  if (streamJustEnded && stream?.type === StreamType.Stream) {
    return null;
  }

  return (
    <>
      <Head
        title={`${data ? `${stream?.name}` : 'Stream loading...'}`}
        description={
          cleanDescription(stream?.description) || stream?.name || ''
        }
        image={stream?.imageURL || ''}
        structuredData={generateVideoObjectSchema(stream)}
        video={videoMeta}
        canonicalLink={canonicalUrl}
      />
      {/* SSR-visible video element for Googlebot — interactive player mounts client-side */}
      <SsrVideoElement
        src={videoMeta?.url}
        poster={stream?.imageURL ?? undefined}
      />
      <AppBannerAndModal
        when={!!stream && !isLoading && !isWatchStreamNotAvailable}
        variant="join"
        username={stream?.store?.slug ?? undefined}
        profileImageUrl={
          stream?.store?.storeDetails?.avatarURL ??
          stream?.imageURL ??
          undefined
        }
      />
      <div className="watchStreamPageRoot">
        <Header fixed />

        {isLoading ||
        isWatchStreamNotAvailable ||
        // While refreshing, data that says "not watchable" is untrusted
        // (anonymous SSR cache says `isPurchased: false` even for buyers) —
        // hold the loader instead of flashing locked content or redirecting.
        (loading && !!isUnavailableFromData) ? (
          <Main withoutBottomPadding>
            <FourDotsLoader />
          </Main>
        ) : (
          <Main className="watchStreamPageRoot__main" withoutBottomPadding>
            <StripeElements>
              <StreamProvider stream={stream}>
                {streamWithRecords ? (
                  <FinishedStream stream={stream} refetchStream={refetch} />
                ) : (
                  <>
                    {stream && (
                      <UnfinishedStream
                        stream={stream}
                        refetchStream={refetch}
                        updateStreamQuery={updateQuery}
                      />
                    )}
                  </>
                )}
              </StreamProvider>
            </StripeElements>
          </Main>
        )}
      </div>

      {isStreamEnded && <Footer />}

      <PurchaseSuccessModal
        open={showPurchaseSuccess}
        onClose={handleClosePurchaseModal}
      />
    </>
  );
};

export { WatchStreamContent };

export default withApollo(WatchStreamContent, { getDataFromTree });
